import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArchiveRestore, Download, Eye, Trash2, Undo2 } from 'lucide-react';
import { useArchiveSubmission, useDeleteSubmission, useFormSubmissions, useSaveChecklist } from '@/api/hooks';
import { api, ApiError } from '@/api/client';
import { submitErrorMessage } from '@/lib/formErrors';
import { useToast } from '@/components/Toast';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { MassChangeUploads, useMassChangeUploads } from '@/components/MassChangeUploads';
import { ChecklistCells, useChecklistEdits } from '@/components/SubmissionChecklist';
import {
  DATE_MSG, FORM_TYPES, ITEMS, LABEL, groupSubmissions, orderedClients, typeLabel, type Detail, type Grouped,
} from '@/lib/submissions';

// SS10 / item 18: Submitted Forms (admin). Worked like Pending Requests: one row per
// client / engagement with the checklist inline, then Submit. A request whose rows are
// all done moves to Completed (its submitter is emailed); View opens the full form.
// Delete is a soft delete (hidden in the app, kept in the table for audit).

type Row = Record<string, unknown>;

// Item 18: two tabs, like the Power BI report.
// Mass Change: one request per uploaded file, worked here (download, import into Maconomy, complete).
type Tab = 'client' | 'engagement' | 'masschange';
const TABS: { value: Tab; label: string; types: string[] }[] = [
  { value: 'client', label: 'Client Requests', types: ['Termination', 'Reactivation'] },
  { value: 'engagement', label: 'Engagement Requests', types: ['Discontinuation'] },
  { value: 'masschange', label: 'Mass Change', types: [] },
];
const TAB_KEY = 'submittedForms.tab';
const loadTab = (): Tab => {
  try {
    const t = localStorage.getItem(TAB_KEY);
    return t === 'engagement' || t === 'masschange' ? t : 'client';
  } catch { return 'client'; }
};

/** The Client / Engagement cell of one row (sub-clients indented under their master). */
function DetailCell({ g, d, isSub }: { g: Grouped; d: Detail; isSub: boolean }) {
  if (g.formType === 'Discontinuation') {
    return (
      <>
        <span className="font-medium">{d.engagementId}</span> · {d.clientName}
        {d.service && <div className="text-xs text-muted-foreground">{d.service}</div>}
      </>
    );
  }
  return isSub ? (
    <div className="pl-4 text-muted-foreground">↳ {d.clientId} · {d.clientName} <span className="text-xs italic">(sub-client)</span></div>
  ) : (
    <>
      {d.clientId} · {d.clientName}
      {d.newMaster && <div className="text-xs text-muted-foreground">(new master: {d.newMaster})</div>}
    </>
  );
}

export function RequestsList() {
  const notify = useToast();
  const [tab, setTabState] = useState<Tab>(loadTab);
  // Filters live in the URL so Back from a request page returns to the same list.
  const [params, setParams] = useSearchParams();
  const formType = params.get('formType') ?? '';
  const search = params.get('q') ?? '';
  const from = params.get('from') ?? '';
  const to = params.get('to') ?? '';
  const archived = params.get('archived') ?? ''; // Show: '' open | 'only' completed | 'all'
  const setParam = (k: string, v: string) =>
    setParams((p) => { const n = new URLSearchParams(p); if (v) n.set(k, v); else n.delete(k); return n; }, { replace: true });
  const [searchInput, setSearchInput] = useState(search);

  const q = useFormSubmissions({ formType, q: search, from, to, archived });
  const save = useSaveChecklist();
  const reopen = useArchiveSubmission();
  const del = useDeleteSubmission();
  const ce = useChecklistEdits();
  const [confirm, setConfirm] = useState<{ kind: 'submit' } | { kind: 'delete'; g: Grouped } | null>(null);
  const groups = useMemo<Grouped[]>(() => groupSubmissions((q.data ?? []) as Row[]), [q.data]);

  const tabDef = TABS.find((t) => t.value === tab)!;
  const tabGroups = groups.filter((g) => tabDef.types.includes(g.formType));
  const items = ITEMS[tabDef.types[0]] ?? [];
  const uploads = useMassChangeUploads('all', undefined, archived);
  const countFor = (t: (typeof TABS)[number]) =>
    t.value === 'masschange' ? (uploads.data ?? []).length : groups.filter((g) => t.types.includes(g.formType)).length;
  function setTab(t: Tab) {
    setTabState(t);
    try { localStorage.setItem(TAB_KEY, t); } catch { /* per-viewer convenience only */ }
    // Form Type filter only lists the current tab's types.
    if (formType && !TABS.find((x) => x.value === t)!.types.includes(formType)) setParam('formType', '');
  }

  const blocked = ce.missingDate(groups).length > 0;

  // Like Pending's Submit: save the edited rows; requests whose rows are now all done
  // move to Completed and their submitters are emailed.
  async function onSubmit() {
    setConfirm(null);
    const missing = ce.missingDate(groups);
    if (missing.length) {
      notify('error', `${DATE_MSG} Missing for: ${missing.map(({ g, d }) => `#${g.id} ${d.engagementId || d.clientId}`).join(', ')}.`);
      return;
    }
    const rows = ce.toSave(groups);
    if (!rows.length) return;
    try {
      const res = await save.mutateAsync(rows);
      ce.reset();
      const done = res?.completed ?? [];
      notify('success', `Saved ${rows.length} row${rows.length === 1 ? '' : 's'}.` + (done.length
        ? ` Request${done.length === 1 ? '' : 's'} #${done.join(', #')} moved to Completed and the submitter was emailed.` : ''));
    } catch (e) {
      notify('error', submitErrorMessage(e));
    }
  }

  async function onReopen(g: Grouped) {
    try {
      await reopen.mutateAsync({ id: g.id, archived: false });
      notify('success', `Request #${g.id} reopened.`);
    } catch (e) {
      notify('error', e instanceof ApiError ? e.problem.title : 'Update failed');
    }
  }

  async function onDelete(g: Grouped) {
    setConfirm(null);
    try {
      await del.mutateAsync(g.id);
      notify('success', `Request #${g.id} deleted.`);
    } catch (e) {
      notify('error', e instanceof ApiError ? e.problem.title : 'Delete failed');
    }
  }

  const hasFilter = formType || search || from || to || archived;

  function exportXlsx() {
    const qs = new URLSearchParams();
    if (formType) qs.set('formType', formType);
    if (search) qs.set('q', search);
    if (from) qs.set('from', from);
    if (to) qs.set('to', to);
    if (archived) qs.set('archived', archived);
    qs.set('group', tab);
    api.download(`/forms/submissions.xlsx?${qs.toString()}`, 'submitted-forms.xlsx');
  }

  const cols = 7 + items.length; // #, Form, Submitted, Effective, Client/Engagement, items, Date Completed, actions

  return (
    <div className="flex h-full flex-col gap-4 p-6">
      {/* Button tabs: outlined in red, the selected one filled, so the two lists stand out. */}
      <div className="flex flex-shrink-0 flex-wrap gap-2" role="tablist">
        {TABS.map((t) => (
          <button key={t.value} type="button" role="tab" aria-selected={tab === t.value}
            className={`rounded-md border-2 border-primary px-5 py-2 text-base font-semibold transition-colors ${tab === t.value
              ? 'bg-primary text-primary-foreground' : 'bg-card text-primary hover:bg-primary/10'}`}
            onClick={() => setTab(t.value)}>
            {t.label} ({countFor(t)})
          </button>
        ))}
      </div>
      {tab === 'masschange' ? (
        <>
          <div className="flex flex-shrink-0 flex-wrap items-end gap-3">
            <div>
              <label className="label">Show</label>
              <select className="input w-32" value={archived} onChange={(e) => setParam('archived', e.target.value)}>
                <option value="">Open</option>
                <option value="only">Completed</option>
                <option value="all">All</option>
              </select>
            </div>
          </div>
          <div className="min-h-0 flex-1 overflow-hidden rounded-lg border border-border bg-card">
            <MassChangeUploads scope="all" status={archived} />
          </div>
          <p className="flex-shrink-0 text-xs text-muted-foreground">
            One request per Mass Change file. Download the file and import it into Maconomy, then set Maconomy (and Constant for
            client files) and the Date Completed, and Submit. A finished request moves to Completed and the submitter is emailed.
            Show: All also lists older uploads, whose changes were sent to Pending Requests.
          </p>
        </>
      ) : (
      <>
      <div className="flex flex-shrink-0 flex-wrap items-end gap-3">
        <div>
          <label className="label">Form Type</label>
          <select className="input w-56" value={formType} onChange={(e) => setParam('formType', e.target.value)}>
            <option value="">All</option>
            {FORM_TYPES.filter((t) => tabDef.types.includes(t.value)).map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>
        <div className="min-w-[220px] flex-1">
          <label className="label">{tab === 'client' ? 'Search (client or submitter)' : 'Search (client, engagement or submitter)'}</label>
          <div className="flex gap-2">
            <input className="input" placeholder={tab === 'client' ? 'Client name / ID or submitter…' : 'Client, engagement ID or submitter…'} value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && setParam('q', searchInput)} />
            <button className="btn-secondary" onClick={() => setParam('q', searchInput)}>Search</button>
          </div>
        </div>
        <div>
          <label className="label">Submitted from</label>
          <input type="date" className="input w-40" value={from} onChange={(e) => setParam('from', e.target.value)} />
        </div>
        <div>
          <label className="label">to</label>
          <input type="date" className="input w-40" value={to} onChange={(e) => setParam('to', e.target.value)} />
        </div>
        <div>
          <label className="label">Show</label>
          <select className="input w-32" value={archived} onChange={(e) => setParam('archived', e.target.value)}>
            <option value="">Open</option>
            <option value="only">Completed</option>
            <option value="all">All</option>
          </select>
        </div>
        {hasFilter && (
          <button className="btn-secondary" onClick={() => { setSearchInput(''); setParams(new URLSearchParams(), { replace: true }); }}>
            Clear
          </button>
        )}
        <div className="ml-auto flex gap-2">
          <button className="btn-secondary flex items-center gap-2" onClick={exportXlsx} disabled={tabGroups.length === 0}>
            <Download className="h-4 w-4" /> Export
          </button>
          <button className="btn-secondary inline-flex items-center gap-1" disabled={!ce.dirty || save.isPending}
            title="Discard unsaved status / date edits" onClick={ce.reset}>
            <Undo2 className="h-4 w-4" /> Discard Changes
          </button>
          <button className="btn-success" disabled={!ce.dirty || blocked || save.isPending} title={blocked ? DATE_MSG : ''}
            onClick={() => setConfirm({ kind: 'submit' })}>
            {save.isPending ? 'Submitting…' : 'Submit'}
          </button>
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-auto rounded-lg border border-border bg-card">
        <table className="w-full text-sm">
          <thead className="table-head">
            <tr className="border-b border-border text-left text-xs uppercase text-muted-foreground">
              <th className="px-3 py-2">#</th>
              <th className="px-3 py-2">Form</th>
              <th className="px-3 py-2">Submitted</th>
              <th className="px-3 py-2">Effective</th>
              <th className="px-3 py-2">{tab === 'client' ? 'Client' : 'Engagement'}</th>
              {items.map((c) => <th key={c} className="px-2 py-2">{LABEL[c]}</th>)}
              <th className="px-2 py-2">Date Completed</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {q.isFetching && tabGroups.length === 0 && (
              <tr><td colSpan={cols} className="px-3 py-4 text-muted-foreground">Loading…</td></tr>
            )}
            {!q.isFetching && tabGroups.length === 0 && (
              <tr><td colSpan={cols} className="px-3 py-4 text-muted-foreground">No submitted forms match.</td></tr>
            )}
            {tabGroups.map((g) => {
              const effective = [...new Set(g.details.map((d) => d.effectiveDate).filter(Boolean))].join(', ');
              const rows = g.formType === 'Discontinuation' ? g.details.map((d) => ({ d, isSub: false })) : orderedClients(g);
              const span = Math.max(rows.length, 1);
              // The request columns (#, form, submitted, effective) and the actions span its rows.
              const head = (
                <>
                  <td rowSpan={span} className="px-3 py-2 font-semibold">{g.id}</td>
                  <td rowSpan={span} className="px-3 py-2">
                    {typeLabel(g.formType)}
                    {g.completed && (
                      <div className="mt-1 text-xs">
                        <span className="rounded bg-green-100 px-1.5 py-0.5 font-medium text-green-800">Completed</span>
                        <div className="mt-0.5 text-muted-foreground">{g.completedAt.split(',')[0]}{g.completedBy && ` by ${g.completedBy}`}</div>
                      </div>
                    )}
                  </td>
                  <td rowSpan={span} className="whitespace-nowrap px-3 py-2">
                    {g.date.split(',')[0]}
                    <div className="text-xs text-muted-foreground">by {g.by}</div>
                  </td>
                  <td rowSpan={span} className="whitespace-nowrap px-3 py-2">{effective}</td>
                </>
              );
              const actions = (
                <td rowSpan={span} className="whitespace-nowrap px-3 py-2 text-right">
                  <div className="flex items-center justify-end gap-2">
                    <Link to={`/forms/submitted/${g.id}`} className="btn-secondary inline-flex items-center gap-1 !py-1 text-xs">
                      <Eye className="h-3.5 w-3.5" /> View
                    </Link>
                    {g.completed && (
                      <button className="btn-secondary inline-flex items-center gap-1 !py-1 text-xs" disabled={reopen.isPending}
                        title="Reopen: back to the open list so the checklist can be changed" onClick={() => void onReopen(g)}>
                        <ArchiveRestore className="h-3.5 w-3.5" /> Reopen
                      </button>
                    )}
                    <button className="text-destructive hover:opacity-70" title="Delete Request" disabled={del.isPending}
                      onClick={() => setConfirm({ kind: 'delete', g })}><Trash2 className="h-4 w-4" /></button>
                  </div>
                </td>
              );
              if (!rows.length) {
                return (
                  <tr key={g.id} className="border-t border-border align-top">
                    {head}
                    <td colSpan={items.length + 2} className="px-3 py-2 text-muted-foreground">No client or engagement rows on this request.</td>
                    {actions}
                  </tr>
                );
              }
              return rows.map(({ d, isSub }, i) => (
                <tr key={d.key} className={`align-top ${i === 0 ? 'border-t border-border' : ''} ${ce.edits[d.key] ? 'bg-warning/10' : ''}`}>
                  {i === 0 && head}
                  <td className="px-3 py-2"><DetailCell g={g} d={d} isSub={isSub} /></td>
                  <ChecklistCells g={g} d={d} ce={ce} cellClass="px-2 py-1.5" />
                  {i === 0 && actions}
                </tr>
              ));
            })}
          </tbody>
        </table>
      </div>
      <ConfirmDialog
        open={confirm !== null}
        message={confirm?.kind === 'delete'
          ? `Delete request #${confirm.g.id} (${typeLabel(confirm.g.formType)}, submitted by ${confirm.g.by})? It will be removed from Submitted Forms. This cannot be undone from the app.`
          : 'Do you wish to submit these changes? Requests with every row done will move to Completed and the submitter will be emailed.'}
        confirmLabel={confirm?.kind === 'delete' ? 'Delete' : 'Submit'}
        cancelLabel="Cancel"
        onConfirm={() => { const c = confirm; if (c?.kind === 'delete') void onDelete(c.g); else void onSubmit(); }}
        onCancel={() => setConfirm(null)}
      />
      <p className="flex-shrink-0 text-xs text-muted-foreground">
        Set the statuses and Date Completed here, then Submit. A request moves to Completed (and its submitter is emailed) once every
        row is done: tracked items Yes or N/A and a Date Completed. Edited rows are highlighted until you Submit. Showing up to 500 most-recent submissions.
      </p>
      </>
      )}
    </div>
  );
}
